/**
 * Analytics event system — single source of truth for product telemetry.
 *
 * Design principles:
 *  1) 근본적 접근 — every event funnels through `logEvent()`; no ad-hoc Firestore writes.
 *  2) 안전성 우선 — every write is wrapped in try/catch; analytics failures NEVER throw
 *     to the host page (a broken Firestore must not break login/checkout/etc).
 *  3) 데이터 무결성 — `buildEvent()` is pure and validates the payload shape before write.
 *  4) 테스트 가능성 — `buildEvent()` is exported so unit tests can assert shape without
 *     touching Firestore or window.
 *
 * Storage:
 *  Collection: `analytics_events` (Task 1 owns the rules: anyone may create,
 *  only admins may read).
 *
 * Open integration points (TODOs):
 *  - Toss payment success handlers in `src/app/payment/success/page.tsx`,
 *    `src/app/payment/maptest-success/page.tsx`, and
 *    `src/app/payment/workbook-success/page.tsx` already log
 *    `logEvent("purchase", { amount, plan, orderId })` after API confirmation.
 */

import { addDoc, collection, doc, serverTimestamp, setDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "./firebase";
import { captureFirstTouch, readFirstTouchCookie } from "./first-touch";
import { clientVisitClass } from "./visit-class";

export type EventType =
  | "page_view"
  | "test_start"
  | "test_complete"
  | "test_abandoned"
  | "purchase"
  | "signup"
  | "login"
  | "credit_used"
  | "workbook_start"
  // Signup-funnel micro events. Only these three — enough to answer
  // "where did the visitor drop off" without over-instrumenting.
  //   signup_form_started   — first interaction (input or social button)
  //   signup_form_submitted — actual submit / OAuth popup launched
  //   signup_error_shown    — error rendered (metadata: { method, error_code })
  | "signup_form_started"
  | "signup_form_submitted"
  | "signup_error_shown"
  // Practice-mode events fired by /practice. Distinct from test_* so
  // funnel queries can isolate the daily-practice loop from the exam.
  //   practice_start         — user committed to a (subject, grade)
  //   practice_answer        — user submitted an answer (metadata: { correct })
  //   practice_limit_reached — daily 8-question cap hit
  | "practice_start"
  | "practice_answer"
  | "practice_limit_reached"
  // App-shell events fired by the /app surface. Distinct from page_view
  // so the marketing-site funnel and the app-shell funnel can be
  // separated in analytics queries.
  //   app_open        — app-shell mounted (first /app/* navigation)
  //   app_tab_change  — bottom-tab navigation (metadata: { tab })
  | "app_open"
  | "app_tab_change"
  // Checkout-funnel events fired by pricing/page.tsx and /payment/fail.
  // Distinct from "purchase" (which only fires on confirmed success) so
  // funnel queries can measure the drop-off between each step:
  //   checkout_click       — 결제 버튼 클릭 (metadata: { planId })
  //   checkout_sdk_fail    — Toss SDK 로드 실패 (metadata: { error })
  //   checkout_intent_ok   — /api/payment/intent 성공 (metadata: { orderId, finalAmount })
  //   checkout_intent_fail — intent POST 실패 (metadata: { error, status })
  //   checkout_toss_opened — requestPayment 호출 직전 (metadata: { orderId, amount })
  //   checkout_toss_dismissed — requestPayment reject 캐치 (metadata: { orderId, code, message })
  //   checkout_toss_failed — /payment/fail 랜딩 (metadata: { orderId, code, message })
  | "checkout_click"
  | "checkout_sdk_fail"
  | "checkout_intent_ok"
  | "checkout_intent_fail"
  | "checkout_toss_opened"
  | "checkout_toss_dismissed"
  | "checkout_toss_failed"
  // MAP TEST Navigator 글 하단 모의고사 CTA 클릭 (2026-10-12 #47 · NoteCta.tsx · metadata: { slug, subject })
  | "note_cta_click"
  // 시험 화면: 답 제출 → 다음 문항 표시 3초 초과 (2026-10-16 · metadata: { ms, testSessionId, qIndex, subject } · src/lib/test-telemetry.ts)
  | "question_transition_slow";

export interface AnalyticsEvent {
  type: EventType;
  uid: string | null;
  sessionId: string;
  page: string;
  // serverTimestamp() returns FieldValue at write time and Timestamp at read time.
  // Using `any` here keeps the interface usable on both sides without leaking
  // Firestore-internal types into call sites.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  timestamp: any;
  metadata: Record<string, unknown>;
}

const SESSION_KEY = "analytics_sid";

/**
 * Resolves (or lazily creates) the per-browser analytics session id.
 * Persisted in localStorage; permanent (no TTL) — a session id is just a
 * stable cohort token, not auth.
 */
export function getOrCreateSessionId(): string {
  if (typeof window === "undefined") {
    // SSR / server-side guard — return ephemeral id; we never write events
    // server-side from this util anyway, so the value is essentially unused.
    return "ssr";
  }
  try {
    const existing = window.localStorage.getItem(SESSION_KEY);
    if (existing && existing.length > 0) return existing;
    const fresh = generateSessionId();
    window.localStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch {
    // Private mode / blocked storage — fall back to a per-call random id.
    return generateSessionId();
  }
}

function generateSessionId(): string {
  try {
    if (
      typeof crypto !== "undefined" &&
      typeof (crypto as Crypto).randomUUID === "function"
    ) {
      return (crypto as Crypto).randomUUID();
    }
  } catch {
    // ignore
  }
  // RFC4122-shaped fallback is overkill — a high-entropy random string is enough.
  return (
    Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)
  );
}

function currentPage(): string {
  return typeof window !== "undefined" ? window.location.pathname : "";
}

function currentUid(): string | null {
  try {
    return getAuth().currentUser?.uid ?? null;
  } catch {
    return null;
  }
}

/** Admin-traffic guard. NEXT_PUBLIC_ADMIN_EMAILS is the same env the
 *  rest of the codebase consults (dashboard/page.tsx, AdminConsole.tsx);
 *  the fallback to "trinityprep103@gmail.com" mirrors api-auth.ts and
 *  admin-guard.ts so a missing env never accidentally widens the set. */
const ADMIN_EMAIL_ALLOWLIST: ReadonlyArray<string> = (
  process.env.NEXT_PUBLIC_ADMIN_EMAILS ?? "trinityprep103@gmail.com"
)
  .split(",")
  .map((e) => e.trim().toLowerCase())
  .filter(Boolean);

function currentIsAdmin(): boolean {
  try {
    const email = (getAuth().currentUser?.email ?? "").trim().toLowerCase();
    return email !== "" && ADMIN_EMAIL_ALLOWLIST.includes(email);
  } catch {
    return false;
  }
}

/** 내부 접속 표시 (localStorage · 관리자 로그인 또는 주소에 ?internal=1 · ?internal=0 으로 해제). */
const INTERNAL_KEY = "mte_internal";
function markInternalBrowser(): void {
  try { window.localStorage.setItem(INTERNAL_KEY, "1"); } catch { /* 저장 차단 — 무시 */ }
}
function currentVisitClass(): "auto" | "internal" | null {
  if (typeof window === "undefined") return null;
  let internalFlag = false;
  try {
    const q = new URLSearchParams(window.location.search).get("internal");
    if (q === "1") window.localStorage.setItem(INTERNAL_KEY, "1");
    if (q === "0") window.localStorage.removeItem(INTERNAL_KEY);
    internalFlag = window.localStorage.getItem(INTERNAL_KEY) === "1";
  } catch { /* 저장 차단 — 내부 표시 없음 */ }
  return clientVisitClass({ userAgent: navigator.userAgent ?? "", webdriver: navigator.webdriver === true, internalFlag });
}

const VALID_TYPES: ReadonlySet<EventType> = new Set<EventType>([
  "page_view",
  "test_start",
  "test_complete",
  "test_abandoned",
  "purchase",
  "signup",
  "login",
  "credit_used",
  "workbook_start",
  "signup_form_started",
  "signup_form_submitted",
  "signup_error_shown",
  "practice_start",
  "practice_answer",
  "practice_limit_reached",
  "app_open",
  "app_tab_change",
  "checkout_click",
  "checkout_sdk_fail",
  "checkout_intent_ok",
  "checkout_intent_fail",
  "checkout_toss_opened",
  "checkout_toss_dismissed",
  "checkout_toss_failed",
  "note_cta_click",
  // 2026-10-26: EventType 에만 있고 여기 빠져 buildEvent 가 throw → 저장 0 이던 결함 (2026-10-16 부터) 수정
  "question_transition_slow",
]);

/**
 * Pure builder — given the dynamic inputs, produce a fully-shaped event.
 * Throws for invalid event types so the caller's try/catch in `logEvent`
 * surfaces programmer error in dev tools without crashing the page.
 */
export function buildEvent(
  type: EventType,
  uid: string | null,
  sessionId: string,
  page: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  timestamp: any,
  metadata: Record<string, unknown> = {}
): AnalyticsEvent {
  if (!VALID_TYPES.has(type)) {
    throw new Error(`[analytics] invalid event type: ${String(type)}`);
  }
  if (typeof sessionId !== "string" || sessionId.length === 0) {
    throw new Error("[analytics] sessionId must be a non-empty string");
  }
  if (typeof page !== "string") {
    throw new Error("[analytics] page must be a string");
  }
  // Strip non-serializable values from metadata defensively.
  const safeMeta: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(metadata)) {
    if (typeof v === "function") continue;
    if (typeof v === "undefined") continue;
    safeMeta[k] = v;
  }
  return {
    type,
    uid,
    sessionId,
    page,
    timestamp,
    metadata: safeMeta,
  };
}

/**
 * Fire-and-forget event log. NEVER throws — analytics failures are swallowed
 * and surfaced only via console.warn in dev. Returns void Promise so callers
 * can `await` if they want to (e.g. before navigation), but they don't have to.
 */
/** 결제 직전 이벤트 — 리디렉션에 쓰기가 끊기지 않게 서버에도 보낸다 (2026-10-26 블록 7-1 · /api/analytics/event ALLOWED_TYPES 와 같은 목록). */
const SURVIVE_NAV_TYPES = new Set<EventType>([
  "checkout_click",
  "checkout_sdk_fail",
  "checkout_intent_ok",
  "checkout_intent_fail",
  "checkout_toss_opened",
  "checkout_toss_dismissed",
  "checkout_toss_failed",
  "purchase",
]);

/** 브라우저·서버가 같은 문서에 쓰도록 이벤트 id 를 미리 정한다 (서버 라우트 EVENT_ID_RE 와 같은 모양). */
function newEventId(): string {
  const rnd = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID().replace(/-/g, "")
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  return `ev_${rnd.slice(0, 32)}`;
}

export async function logEvent(
  type: EventType,
  metadata: Record<string, unknown> = {}
): Promise<void> {
  // Skip everything for admin users (QA / internal browsing) so dashboards
  // aren't polluted by the team's own traffic. Suppresses BOTH the
  // analytics_events Firestore write below AND the /api/analytics/visit
  // POST a few lines down — the early return is intentionally before
  // either side effect runs. Non-logged-in users and regular customers
  // keep flowing through unchanged.
  if (currentIsAdmin()) {
    // 관리자로 로그인한 적 있는 브라우저 표시 — 로그아웃 뒤 같은 브라우저 방문도 "내부" 로 보고에서 뺀다 (2026-10-26 · src/lib/visit-class.ts)
    markInternalBrowser();
    return;
  }

  // Mirror every page_view to /api/analytics/visit so the server can
  // read the real client IP/UA from the request headers and produce the
  // sha256(IP|UA) visitor hash the admin unique-visitor cards rely on.
  // The hash definition and the analytics_visits write path are
  // unchanged — what moved is the trigger: previously the edge proxy
  // fired this fetch fire-and-forget, but Vercel's edge runtime tears
  // down in-flight requests on return, so most visit writes were
  // getting dropped. Doing it from the browser keeps the request alive
  // on the normal navigation network stack, matching the reliability
  // we already see for the client-side Firestore event write below.
  //
  // Fire-and-forget on purpose: never block the caller, never throw.
  if (type === "page_view" && typeof window !== "undefined") {
    void fetch("/api/analytics/visit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path: window.location.pathname }),
    }).catch(() => { /* swallow — analytics failure must not surface */ });
  }

  // 방문 분류 (2026-10-26 · 주간 보고 후속 ②) — 봇·헤드리스·관리자·내부 확인 접속이면 cl 을 붙여 보고의 "사람 방문" 에서 뺀다 (사람이면 필드 없음).
  if (type === "page_view") {
    const cl = currentVisitClass();
    if (cl) metadata = { ...metadata, cl };
  }

  // 유입 경로 (2026-10-13 작업 7 · src/lib/first-touch.ts) — page_view 에 첫 유입 경로를 붙여 관리자 유입·전환 대시보드가 방문을 경로별로 센다.
  // 페이지 effect 가 루트 레이아웃 effect (FirstTouchCapture) 보다 먼저 돌 수 있어 여기서도 먼저 저장 (멱등).
  if (type === "page_view") {
    try {
      captureFirstTouch();
      const ft = readFirstTouchCookie();
      if (ft) metadata = { ...metadata, ft: { s: ft.utm_source, m: ft.utm_medium, c: ft.utm_campaign, r: ft.referrer } };
    } catch {
      /* 쿠키 차단 등 — 무시 */
    }
  }

  // 서버 미러 (2026-10-26 RIT 재정비 블록 7-1): Toss 리디렉션으로 브라우저의 Firestore 쓰기가
  // 끊기는 결함 보완 — checkout_* / purchase 는 `fetch(..., { keepalive: true })` 로 서버에도 보낸다.
  // 중복 0: 이벤트마다 같은 문서 id 를 정해 브라우저·서버 둘 다 그 id 로 create (먼저 쓴 쪽만 저장 · 나중 쪽은 이미 있어 실패).
  const mirrorId = SURVIVE_NAV_TYPES.has(type) && typeof window !== "undefined" ? newEventId() : null;
  if (mirrorId) {
    try {
      void fetch("/api/analytics/event", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          eventId: mirrorId,
          type,
          uid: currentUid(),
          sessionId: getOrCreateSessionId(),
          page: currentPage(),
          metadata,
          clientAtMs: Date.now(),
        }),
      }).catch(() => { /* 서버 미러 실패는 조용히 무시 — 브라우저 쓰기가 기본 경로 */ });
    } catch {
      /* keepalive 미지원 브라우저 — 조용히 무시 */
    }
  }

  try {
    if (!db) return;
    const event = buildEvent(
      type,
      currentUid(),
      getOrCreateSessionId(),
      currentPage(),
      serverTimestamp(),
      metadata
    );
    // 서버 미러가 먼저 같은 id 로 썼으면 이 쓰기는 규칙 (create 만 허용) 에 막혀 조용히 실패 — 중복 0.
    if (mirrorId) await setDoc(doc(db, "analytics_events", mirrorId), event);
    else await addDoc(collection(db, "analytics_events"), event);
  } catch (err) {
    if (typeof console !== "undefined" && typeof console.warn === "function") {
      console.warn("[analytics] logEvent failed (suppressed):", err);
    }
  }
}
